import axios from "axios";
import serialize from "json-form-data";
import {addAuthKey} from '@/helpers/addAuthKeyToUrl';
import { BONUS_GROUP_ALT } from "@/constants/fineAndBonusGroups";

const apiUrl = "index.php";
const ARCHIVED = 1;
const NOT_ARCHIVED = 0;

const setBonusArchiveStatus = (state, bonusId, categoryId, status) => {
    if (categoryId) {
        const group = state.bonusGroups.find((g) => g.id.toString() === categoryId.toString());
        const bonus = group.items.find((v) => v.id === bonusId);
        bonus.is_archive = status;
        return;
    }
    const bonus = state.bonuses.find((s) => s.id.toString() === bonusId.toString());
    bonus.is_archive = status;
};

const setQueryParams = (filters, queryParams) => {
    if (filters.status && filters.status !== "all") {
        queryParams.push(`status_type=${filters.status === "archived" ? "archive" : "active"}`);
    }
    if (filters.positions && filters.positions.length) {
        const positions = filters.positions.map((p) => (filters.positionsExcept ? `-${p.value}` : p.value));
        queryParams.push(`post_codes=${positions.join(",")}`);
    }
    if (filters.name) {
        queryParams.push(`search=${filters.name}`);
    }
};

const findAndUpdateBonusEntity = (bonuses, updatedBonus) => {
    const bonus = bonuses.find((v) => v.id.toString() === updatedBonus.id.toString());
    if (bonus) Object.keys(updatedBonus).forEach((key) => {
        bonus[key] = updatedBonus[key];
    });
};

const state = () => ({
    bonusGroups: [],
    bonuses: [],
    positions: [],
    lastBonusGroupIdOfCreatedItem: 0,
    lastChangeWasGroupSorting: false,

    collections: {
        bonuses: [],
    },
});

const getters = {
    getBonusesByGroupId: (state) => (groupId) => state.bonusGroups.find((g) => groupId === g.id).items,
    getBonuses: (state) => (bonusId) => {
        const bonusWithoutGroup = state.bonuses.find((v) => v.id.toString() === bonusId.toString());
        if (bonusWithoutGroup) {
            return bonusWithoutGroup;
        }

        const groupsBonuses = state.bonusGroups.reduce((acc, currentGroup) => {
            if (currentGroup.items && currentGroup.items.length) {
                acc = [...acc, ...currentGroup.items];
            }
            return acc;
        }, []);

        return groupsBonuses.find((v) => v.id.toString() === bonusId.toString());
    },
    isAnyArchivedItem: (state) => {
        const allViolations = state.bonusGroups.reduce((acc, currentGroup) => {
            if (currentGroup.items && currentGroup.items.length) {
                acc = [...acc, ...currentGroup.items];
            }
            return acc;
        }, []);
        return [...state.bonuses, ...allViolations].some((v) => v.is_archive.toString() === "1");
    },
    isAnyItemExists: (state) => state.bonuses.length || state.bonusGroups.length,
    getBonusesCollection: (state) => state.collections.bonuses,
};

const mutations = {
    setPositions(state, positions) {
        state.positions = positions;
    },
    setBonuses(state, bonuses) {
        state.bonuses = bonuses;
    },
    setBonusesCollection(state, bonuses) {
        state.collections.bonuses = bonuses;
    },
    setBonusesToGroup(state, { id, items }) {
        const group = state.bonusGroups.find((group) => group.id === id);
        group.items = items;
    },
    setBonusGroups(state, groups) {
        state.bonusGroups = [];
        state.bonusGroups = groups
            .map((group) => {
                const bonusPosts = group.bonusPosts ? group.bonusPosts : [];
                return { ...group, items: group.items ? group.items : [], bonusPosts };
            });
    },
    putNewBonus(state, { model, categoryId }) {
        const newBonus = {
            ...model,
            bonusPosts: [],
            is_archive: 0,
        };
        if (model.post_codes) model.post_codes.forEach((code) => {
            newBonus.bonusPosts.push({
                post_code: code,
            });
        });
        if (!categoryId) {
            state.bonuses.unshift(newBonus);
            return;
        }
        state.lastBonusGroupIdOfCreatedItem = categoryId;
        const group = state.bonusGroups.find((g) => g.id.toString() === categoryId.toString());
        if (!group.items) {
            return;
        }
        group.items.unshift(newBonus);
    },
    putNewBonusGroup(state, group) {
        state.bonusGroups.unshift({ ...group, hasItems: 0, items: [] });
    },
    updateBonus(state, bonusData) {
        if (!bonusData.category_id) {
            findAndUpdateBonusEntity(state.bonuses, bonusData);
            return;
        }

        const groupsBonuses = state.bonusGroups.reduce((acc, currentGroup) => {
            if (currentGroup.items && currentGroup.items.length) {
                acc = [...acc, ...currentGroup.items];
            }
            return acc;
        }, []);

        findAndUpdateBonusEntity(groupsBonuses, bonusData);
    },
    updateBonusGroup(state, updatedGroup) {
        const group = state.bonusGroups.find((g) => g.id === updatedGroup.id);
        Object.keys(group).forEach((key) => {
            if (key=="hasArchivedItems" || key=="hasActiveItems" || key=="hasItems") return;
            if (key in updatedGroup) {
                group[key] = updatedGroup[key];
            }
        });
    },
    deleteBonus(state, { id, categoryId }) {
        if (!categoryId) {
            state.bonuses = state.bonuses.filter((s) => s.id !== id);
            return;
        }
        const group = state.bonusGroups.find((group) => group.id.toString() === categoryId.toString());
        group.items = group.items.filter((i) => i.id !== id);
        if (!group.items.length) {
            group.hasItems = 0;
            group.hasArchivedItems = 0;
        }
    },
    deleteBonusGroup(state, id) {
        const groupIndex = state.bonusGroups.findIndex((g) => g.id === id);
        state.bonusGroups.splice(groupIndex, 1);
    },
    moveBonusToArchive(state, { bonusId, categoryId }) {
        setBonusArchiveStatus(state, bonusId, categoryId, ARCHIVED);
    },
    moveBonusFromArchive(state, { bonusId, categoryId }) {
        setBonusArchiveStatus(state, bonusId, categoryId, NOT_ARCHIVED);
    },
    updateBonusCategory(state, { categoryId, bonus }) {
        const group = state.bonusGroups.find((g) => g.id === categoryId);
        if (!group) {
            state.bonuses.find((v) => v.id === bonus.id).category_id = null;
            return;
        }
        group.items.find((v) => v.id === bonus.id).category_id = categoryId;
    },
    setLastChangeWasGroupSortingValue(state, value) {
        state.lastChangeWasGroupSorting = value;
    },
};

const actions = {
    async fetchPositions({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/directory/position`, token);

        const { data } = await axios.get(url);

        const positions = [];
        Object.entries(data).forEach(([positionsCode, position]) => {
            positions.push({
                label: position.name,
                value: positionsCode,
            });
        });
        commit("setPositions", positions);
    },
    async fetchBonuses({ rootState, commit }, filters) {
        const token = rootState.authToken;

        const queryParams = [];

        queryParams.push(`categories=${filters.categories.length ? filters.categories.join(",") : null}`);

        setQueryParams(filters, queryParams);

        const url = addAuthKey(`${apiUrl}/?r=api/competency/index&${queryParams.join("&")}&sort=-sort`, token);

        const { data } = await axios.get(url);

        const [firstCategory] = filters.categories;

        if (firstCategory && filters.categories.length === 1) {
            commit("setBonusesToGroup", { items: data, id: firstCategory });
            return;
        }
        commit("setBonuses", data);
    },
    async getBonusesCollection({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}/?r=api/competency/index`, token);
        const { data } = await axios.get(url);
        commit("setBonusesCollection", data);
    },
    async fetchBonusesGroups({ rootState, commit }, filters) {
        const token = rootState.authToken;
        const queryParams = [];

        if (filters) {
            setQueryParams(filters, queryParams);
        }

        const url = addAuthKey(`${apiUrl}/?r=api/competency-category/index&${queryParams.join("&")}&sort=-sort`, token);

        const { data } = await axios.get(url);
        commit("setBonusGroups", data);
    },
    async createBonus({ rootState, commit }, bonusData) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}/?r=api/competency/create`, token);

        let formatedBonusData = {...bonusData}

        let formatedBonusData1 = Object.entries(formatedBonusData).map(([key, value]) => ([`params[${key}]`, value]))
        
        let formatedBonusData2 = Object.fromEntries(formatedBonusData1)

        let formData = serialize(formatedBonusData2);

        if (bonusData.files) bonusData.files.forEach((file, i) => {
            formData.append(`files[${i}]`, file);
        });

        const { data } = await axios.post(url, formData);

        if (data.success) {
            commit("putNewBonus", {
                model: {
                    ...data.model,
                    post_codes: bonusData.post_codes,
                },
                categoryId: bonusData.category_id,
            });
        }

        return data.success;
    },
    async createBonusGroup({ rootState, commit }, groupName) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/competency-category/create`, token);

        const { data } = await axios.post(url, { name: groupName });

        if (data.success) {
            commit("putNewBonusGroup", data.model);
        }

        return data;
    },
    async changeBonusCategory({ rootState, commit }, { categoryId, bonus }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/competency/update&id=${bonus.id}`, token);

        await axios.put(url, { params: {category_id: categoryId }});

        commit("updateBonusCategory", { categoryId, bonus });
    },
    async changeBonusesSort({ rootState }, sortingData) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/competency/update-sort`, token);

        await axios.patch(url, sortingData);
    },
    async deleteBonus({ rootState, commit }, { id, categoryId }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}/?r=api/competency/delete&id=${id}`, token);

        const { data } = await axios.delete(url);

        if (data.success) {
            commit("deleteBonus", { id, categoryId });
        }
    },
    async deleteBonusGroup({ rootState, commit }, id) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}/?r=api/competency-category/delete&id=${id}`, token);

        const { data } = await axios.delete(url);

        if (data.success) {
            commit("deleteBonusGroup", id);
        }
    },
    async moveBonusToArchive({ rootState, commit }, { bonusId, categoryId }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}/?r=api/competency/archive&id=${bonusId}`, token);

        const { data } = await axios.patch(url);

        if (data.success) {
            commit("moveBonusToArchive", { bonusId, categoryId });
        }
        return data.success;
    },
    async moveBonusFromArchive({ rootState, commit }, { bonusId, categoryId }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}/?r=api/competency/archive&id=${bonusId}&is_archive=0`, token);

        const { data } = await axios.patch(url);

        if (data.success) {
            commit("moveBonusFromArchive", { bonusId, categoryId });
        }

        return data.success;
    },
    async updateBonus({ rootState, commit }, bonusData) {
        const token = rootState.authToken;
        const bonusId = bonusData.id;
        const url = addAuthKey(`${apiUrl}/?r=api/competency/update&id=${bonusId}`, token);

        let formatedBonusData = {...bonusData}

        let formatedBonusData1 = Object.entries(formatedBonusData).map(([key, value]) => ([`params[${key}]`, value]))
        
        let formatedBonusData2 = Object.fromEntries(formatedBonusData1)

        let formData = serialize(formatedBonusData2);

        if (bonusData.files) bonusData.files.forEach((file, i) => {
            formData.append(`files[${i}]`, file);
        });

        const { data } = await axios.put(url, formData);

        if (data.success) {
            commit("updateBonus", {
                ...data.model,
                // post_codes: bonusData.post_codes,
            });
        }

        return data.success;
    },
    async updateBonusGroup({ rootState, commit }, bonusGroupData) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/competency-category/update&id=${bonusGroupData.id}`, token);

        const { data } = await axios.put(url, bonusGroupData);

        if (data.success) {
            commit("updateBonusGroup", data.model);
        }

        return data;
    },
    async updateGroupsSort({ rootState, state, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/competency-category/update-sort`, token);

        const sortingData = state.bonusGroups.map((group, groupIndex) => ({
            id: group.id,
            sort: state.bonusGroups.length - groupIndex,
        }));

        commit("setLastChangeWasGroupSortingValue", false);

        await axios.patch(url, sortingData);
    },
    async updateSort({ rootState, state }, categoryId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}/?r=api/competency/update-sort`, token);

        const sortingData = [];

        if (!categoryId) {
            state.bonuses.forEach((v, vIndex) => {
                sortingData.push({ id: v.id, sort: state.bonuses.length - vIndex });
            });
        } else {
            const group = state.bonusGroups.find((g) => g.id === categoryId);
            group.items.forEach((v, vIndex) => {
                sortingData.push({ id: v.id, sort: group.items.length - vIndex });
            });
        }

        await axios.patch(url, sortingData, {
            headers: {
                "Content-Type": "application/json",
            },
        });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
